import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
} from "reactstrap";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import "../login/mobileNumberVerification.css";
import "./employee-register-bwai.css";
import { toastContainer } from "../../utils/toast";
import { connect } from "react-redux";
import * as url from "../../utils/url";
import axios from "../../axios";
import { withRouter } from "react-router";
import RegistrationResult from "../../components/RegistrationResult";
import RegistrationForm from "../../components/RegistrationForm";
import VerificationCodeEntry from "../../components/VerificationCodeEntry";
import loadingSpinner from "../../assets/spinner.gif";
import {
  REGISTRATION_ALL_FIELDS,
  REGISTRATION_EITHER_IDENTIFIER,
  REGISTRATION_EMAIL_NOT_VALIDATED,
  REGISTRATION_NO_MATCH,
  REGISTRATION_PHONE_TAKEN,
  REGISTRATION_RATE_LIMITED,
} from "../../utils/registrationValidationStrings";
import { getBrandStyle } from "../../utils/brandColor";

// A rate-limited config load is retried (after the server's wait) this many times before the error screen.
const MAX_CONFIG_RETRIES = 3;
const MAX_CONFIG_RETRY_WAIT_SECONDS = 60;
// Same default country as RegistrationForm's phone field and the BE's isPhoneNumberValid.
const PHONE_DEFAULT_COUNTRY = "US";

// bug929: the backend sends { errors: { status, message, retryAfterSeconds } } on failures.
const getErrorInfo = (err) => {
  const errors = err && err.response && err.response.data && err.response.data.errors;
  return {
    status: errors && errors.status,
    message: errors && errors.message,
  };
};

const EmployeeNumberEntry = ({ history }) => {
  const isMobile = window.innerWidth < 768;

  const urlParams = new URLSearchParams(window.location.search);
  const orgId = urlParams.get("orgId");

  // Pre-fill fields from QR code query params (set by SMS Routing Modal)
  const [phoneNumber, setPhoneNumber] = useState(urlParams.get("phone") || "");
  const [firstName, setFirstName] = useState(urlParams.get("firstName") || "");
  const [lastName, setLastName] = useState(urlParams.get("lastName") || "");
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [message, setMessage] = useState("");
  const [currentStep, setCurrentStep] = useState("form"); // "form", "verification", "result"
  const [isLoading, setIsLoading] = useState(false);

  // bug929: company branding + which identifiers the form must ask for.
  const [config, setConfig] = useState(null);
  const [configStatus, setConfigStatus] = useState("loading"); // "loading", "ready", "error"
  const [email, setEmail] = useState("");
  const [userId, setUserId] = useState("");
  const [formError, setFormError] = useState("");
  const configRetryTimer = useRef(null);

  const loadConfig = (attempt) => {
    const tries = typeof attempt === "number" ? attempt : 0;
    if (!orgId) {
      setConfigStatus("error");
      return;
    }
    setConfigStatus("loading");
    axios
      .get(url.registrationConfig, { params: { orgId: orgId } })
      .then(({ data }) => {
        setConfig(data.data);
        setConfigStatus("ready");
      })
      .catch((err) => {
        const response = err && err.response;
        if (response && response.status === 429 && tries < MAX_CONFIG_RETRIES) {
          const errors = response.data && response.data.errors;
          const waitSeconds = Math.min(
            Number(errors && errors.retryAfterSeconds) || 5,
            MAX_CONFIG_RETRY_WAIT_SECONDS
          );
          configRetryTimer.current = setTimeout(() => loadConfig(tries + 1), waitSeconds * 1000);
          return;
        }
        console.error("Error loading registration settings:", err);
        setConfigStatus("error");
      });
  };

  useEffect(() => {
    loadConfig();
    return () => clearTimeout(configRetryTimer.current);
  }, []);

  const validationOn = Boolean(config && config.requireUserValidation);
  const hasEmails = Boolean(config && config.hasEmails);
  const hasUserIds = Boolean(config && config.hasUserIds);
  // Field matrix (ticket 10/16 dropped the User-ID-only form): Email always shows; User ID shows
  // when the roster has User IDs (Email or User ID mode), or when it has neither identifier.
  const showEmail = validationOn;
  const showUserId = validationOn && (hasUserIds || !hasEmails);
  const eitherMode = showEmail && showUserId;
  const instruction = eitherMode ? REGISTRATION_EITHER_IDENTIFIER : REGISTRATION_ALL_FIELDS;

  // bug929 CTO feedback: the brand colour also frames the page (background + card border),
  // not just the submit button. Shares RegistrationForm's own colour so both stay in lockstep.
  const { brandClass, brandStyle } = getBrandStyle(config ? config.brandColor : null);

  // CTO feedback: editing any field after a validation failure should clear that
  // failure's warning (and re-enable Submit) rather than leaving it stuck until resubmit.
  const clearErrorThen = (setter) => (value) => {
    setFormError("");
    setter(value);
  };

  const hasRequiredIdentifier = () => {
    if (!validationOn) return true;
    if (eitherMode) return Boolean(email.trim() || userId.trim());
    if (showEmail) return Boolean(email.trim());
    return Boolean(userId.trim());
  };

  // Feedback 10/21: Submit stays disabled until every field this mode requires is filled. The phone
  // must have a full number's length: the widget already emits "+15" after one typed digit.
  const isFormComplete = Boolean(
    firstName.trim() &&
      lastName.trim() &&
      phoneNumber &&
      isPossiblePhoneNumber(phoneNumber, PHONE_DEFAULT_COUNTRY) &&
      orgId &&
      hasRequiredIdentifier()
  );

  // Used by the first send and by every resend, so the identifiers are never dropped.
  const buildSendPayload = () => {
    const payload = {
      phoneNumber: phoneNumber,
      organizationId: orgId,
      firstName: firstName,
      lastName: lastName,
    };
    if (showEmail) payload.email = email.trim();
    if (showUserId) payload.userId = userId.trim();
    return payload;
  };

  // The no-match / phone-taken failures keep what was typed and show the exact copy on the form.
  const showFormFailure = (status, serverMessage) => {
    if (status === "no_match") {
      setFormError(REGISTRATION_NO_MATCH);
    } else if (status === "phone_taken") {
      setFormError(REGISTRATION_PHONE_TAKEN);
    } else if (status === "email_not_validated") {
      setFormError(REGISTRATION_EMAIL_NOT_VALIDATED);
    } else if (status === "rate_limited") {
      setFormError(serverMessage || REGISTRATION_RATE_LIMITED);
    } else {
      setFormError(serverMessage || instruction);
    }
    setCurrentStep("form");
    // missing_fields means the company changed its settings after this page loaded: re-read them.
    if (status === "missing_fields") {
      loadConfig();
    }
  };

  const handleTryAgain = () => {
    setSubmissionStatus(null);
    setMessage("");
    setPhoneNumber("");
    setFirstName("");
    setLastName("");
    setEmail("");
    setUserId("");
    setFormError("");
    setCurrentStep("form");
  };

  const handleRequestNewCode = () => {
    // Go back to verification step and resend code
    setSubmissionStatus(null);
    setMessage("");
    setCurrentStep("verification");
    handleResendCode();
  };

  const handleChangeNumber = () => {
    // Go back to form step to allow changing phone number
    setCurrentStep("form");
    setSubmissionStatus(null);
    setMessage("");
  };

  const handleResendCode = () => {
    if (!phoneNumber || !orgId) {
      toastContainer("Missing phone number or organization.", "error");
      return;
    }

    setIsLoading(true);

    const payload = buildSendPayload();

    axios
      .post(url.qrCodeSendVerification, payload)
      .then(({ data }) => {
        const status = data.data.status;
        if (status === "verification_sent") {
          toastContainer("Verification code resent successfully!", "success");
        } else if (status === "already_registered") {
          setSubmissionStatus("already_registered");
          setMessage("You are already registered with this phone number.");
          setCurrentStep("result");
        }
      })
      .catch((err) => {
        const info = getErrorInfo(err);
        if (info.status === "rate_limited") {
          toastContainer(info.message || REGISTRATION_RATE_LIMITED, "error");
        } else if (
          info.status === "no_match" ||
          info.status === "phone_taken" ||
          info.status === "email_not_validated" ||
          info.status === "missing_fields"
        ) {
          showFormFailure(info.status, info.message);
        } else {
          console.error("Error resending code:", err);
          toastContainer("Failed to resend code. Please try again.", "error");
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSubmit = function (e) {
    e.preventDefault();
    if (isLoading) return;
    setFormError("");
    if (isFormComplete) {
      setIsLoading(true);

      const payload = buildSendPayload();

      axios
        .post(url.qrCodeSendVerification, payload)
        .then(({ data }) => {
          const status = data.data.status;
          if (status === "verification_sent") {
            // Move to verification step
            setCurrentStep("verification");
          } else if (status === "already_registered") {
            setSubmissionStatus("already_registered");
            setMessage("You are already registered with this phone number.");
            setCurrentStep("result");
          } else {
            setSubmissionStatus("error");
            setMessage(
              "We're unable to process your request at the moment. Please try again shortly."
            );
            setCurrentStep("result");
          }
        })
        .catch((err) => {
          const info = getErrorInfo(err);
          if (
            info.status === "no_match" ||
            info.status === "phone_taken" ||
            info.status === "email_not_validated" ||
            info.status === "missing_fields" ||
            info.status === "rate_limited"
          ) {
            showFormFailure(info.status, info.message);
            return;
          }
          const errorMessage = info.message;
          if (errorMessage === "Invalid phone number format") {
            setSubmissionStatus("error");
            setMessage("The number you entered can not be used to register.");
          } else {
            console.error("Unexpected error:", err);
            setSubmissionStatus("error");
            setMessage("An unexpected error occurred.");
          }
          setCurrentStep("result");
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else if (validationOn) {
      setFormError(instruction);
    } else {
      toastContainer(
        "Please enter your first name, last name, and a valid phone number.",
        "error"
      );
    }
  };

  const handleVerifyCode = (verificationCode) => {
    if (!verificationCode || verificationCode.length !== 4) {
      toastContainer("Please enter a valid 4-digit code.", "error");
      return;
    }

    setIsLoading(true);

    const payload = {
      phoneNumber: phoneNumber,
      organizationId: orgId,
      verificationCode: verificationCode,
    };

    axios
      .post(url.qrCodeVerifyAndRegister, payload)
      .then(({ data }) => {
        const status = data.data.status;
        if (status === "created") {
          setSubmissionStatus("success");
          setMessage(" You’ll get occasional texts to stay informed and voice your input, when needed.");
          setCurrentStep("result");
        } else {
          setSubmissionStatus("error");
          setMessage(
            "We're unable to process your request at the moment. Please try again shortly."
          );
          setCurrentStep("result");
        }
      })
      .catch((err) => {
        const errorStatus = err.response && err.response.data && err.response.data.data && err.response.data.data.status;
        const errorMessage = err.response && err.response.data && err.response.data.errors && err.response.data.errors.message;
        const failureStatus = getErrorInfo(err).status;

        if (failureStatus === "no_match" || failureStatus === "phone_taken") {
          showFormFailure(failureStatus, errorMessage);
        } else if (errorStatus === "expired" || (errorMessage && errorMessage.includes("expired"))) {
          setSubmissionStatus("expired");
          setMessage("Verification code has expired. Please request a new code.");
          setCurrentStep("result");
        } else if (errorStatus === "invalid_code" || (errorMessage && errorMessage.includes("Invalid verification code"))) {
          setSubmissionStatus("invalid_code");
          setMessage("Invalid verification code. Please try again.");
          setCurrentStep("result");
        } else if (errorMessage === "No verification code found. Please request a new code.") {
          setSubmissionStatus("expired");
          setMessage("No verification code found. Please request a new code.");
          setCurrentStep("result");
        } else {
          console.error("Unexpected error:", err);
          setSubmissionStatus("error");
          setMessage("An unexpected error occurred.");
          setCurrentStep("result");
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleTryAgainForInvalidCode = () => {
    // Go back to verification step to retry entering the code
    setSubmissionStatus(null);
    setMessage("");
    setCurrentStep("verification");
  };

  const renderContent = () => {
    // bug929: never guess the form: the identifier fields depend on the company's settings.
    if (configStatus === "loading") {
      return (
        <div className="bwai-reg-loading">
          <img src={loadingSpinner} alt="Loading" height="40" />
        </div>
      );
    }

    if (configStatus === "error") {
      return (
        <RegistrationResult
          status="error"
          message="We're unable to process your request at the moment. Please try again shortly."
          onTryAgain={loadConfig}
        />
      );
    }

    if (currentStep === "result" && submissionStatus) {
      return (
        <RegistrationResult
          status={submissionStatus}
          message={message}
          onTryAgain={
            submissionStatus === "invalid_code"
              ? handleTryAgainForInvalidCode
              : handleTryAgain
          }
          onRequestNewCode={handleRequestNewCode}
        />
      );
    }

    if (currentStep === "verification") {
      return (
        <VerificationCodeEntry
          phoneNumber={phoneNumber}
          onVerify={handleVerifyCode}
          onResendCode={handleResendCode}
          onChangeNumber={handleChangeNumber}
          isLoading={isLoading}
        />
      );
    }

    // Default: form step
    return (
      <RegistrationForm
        phoneNumber={phoneNumber}
        setPhoneNumber={clearErrorThen(setPhoneNumber)}
        firstName={firstName}
        setFirstName={clearErrorThen(setFirstName)}
        lastName={lastName}
        setLastName={clearErrorThen(setLastName)}
        onSubmit={handleSubmit}
        companyName={config ? config.companyName : ""}
        logoUrl={config ? config.logoUrl : null}
        brandColor={config ? config.brandColor : null}
        instruction={instruction}
        showEmail={showEmail}
        showUserId={showUserId}
        email={email}
        setEmail={clearErrorThen(setEmail)}
        userId={userId}
        setUserId={clearErrorThen(setUserId)}
        formError={formError}
        isFormComplete={isFormComplete}
        isLoading={isLoading}
      />
    );
  };

  return (
    <div
      className={`${isMobile ? "mfa-setup--mobile mfa-setup--mobile-padding" : "mfa-setup"
        } ${brandClass}`}
      style={brandStyle}
    >
      <Container className="mfa-container bwai-reg-container">
        <Row className="justify-content-center">
          <Col xs="12" sm="12" md="8" lg="5">
            <Card
              className={`mfa-card ${brandClass} ${isMobile && !brandClass ? "border-0 shadow-none bg-transparent" : ""
                }`}
              style={isMobile ? { width: "100%" } : {}}
            >
              <CardBody
                style={{
                  padding: isMobile ? "20px" : "40px",
                  minHeight: isMobile ? "400px" : "450px",
                }}
              >
                {renderContent()}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default connect()(withRouter(EmployeeNumberEntry));
